import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { scopeToSite } from '../../lib/siteScope';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { IconDownload } from '@tabler/icons-react';

export default function ComplianceReport() {
  const { orgId, userProfile, role } = useAuth();
  const siteId = userProfile?.siteId;
  const [machines, setMachines] = useState([]);
  const [allChecks, setAllChecks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const machinesSnap = await getDocs(collection(db, 'organisations', orgId, 'machines'));
        const machineList = scopeToSite(machinesSnap.docs.map(d => ({ id: d.id, ...d.data() })), role, siteId);
        setMachines(machineList);
        const checks = [];
        await Promise.all(machineList.map(async (machine) => {
          const snap = await getDocs(query(
            collection(db, 'organisations', orgId, 'machines', machine.id, 'checkRecords'),
            orderBy('submittedAt', 'desc'), limit(30)
          ));
          snap.docs.forEach(d => checks.push({ id: d.id, machineId: machine.id, ...d.data() }));
        }));
        setAllChecks(checks);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const passed = allChecks.filter(c => c.result === 'pass').length;
  const failed = allChecks.filter(c => c.result === 'fail').length;
  const passRate = allChecks.length ? Math.round((passed / allChecks.length) * 100) : 0;
  const colour = passRate >= 90 ? 'var(--color-success)' : passRate >= 75 ? 'var(--color-warning)' : 'var(--color-danger)';

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="Compliance report"
          subtitle="Last 30 checks per machine"
          actions={<Button variant="secondary" icon={<IconDownload size={14} stroke={1.5} />} onClick={() => toast.info('PDF export coming soon')}>Export PDF</Button>}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total checks', value: allChecks.length, colour: 'var(--color-text-primary)' },
            { label: 'Passed', value: passed, colour: 'var(--color-success)' },
            { label: 'Failed', value: failed, colour: failed > 0 ? 'var(--color-danger)' : 'var(--color-success)' },
            { label: 'Pass rate', value: passRate + '%', colour },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>
        <Card padding="0">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {['Machine', 'Total checks', 'Failed', 'Pass rate'].map(h => (
              <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>
          {loading ? <LoadingSkeleton rows={5} /> : machines.map((m, i) => {
            const mChecks = allChecks.filter(c => c.machineId === m.id);
            const mPassed = mChecks.filter(c => c.result === 'pass').length;
            const mFailed = mChecks.filter(c => c.result === 'fail').length;
            const mRate = mChecks.length ? Math.round((mPassed / mChecks.length) * 100) : null;
            const mColour = mRate === null ? 'var(--color-text-tertiary)' : mRate >= 90 ? 'var(--color-success)' : mRate >= 75 ? 'var(--color-warning)' : 'var(--color-danger)';
            return (
              <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '11px 16px', borderBottom: i < machines.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', alignItems: 'center' }}>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{m.makeLabel} {m.modelLabel}</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{mChecks.length}</div>
                <div style={{ fontSize: 13, color: mFailed > 0 ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>{mFailed}</div>
                <div style={{ fontSize: 13, color: mColour, fontWeight: 400 }}>{mRate !== null ? mRate + '%' : '—'}</div>
              </div>
            );
          })}
        </Card>
      </div>
    </Layout>
  );
}